import React, { useState, useEffect } from 'react';
import { Box, Stack, LinearProgress, Tooltip, List, ListItem, ListItemText, IconButton, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { InfoOutlined } from '@mui/icons-material';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { db } from 'src/firebase/firebaseConfig';
import { Gig, StatusTimestamp, RootState, FirebaseEvent } from 'shared/types/appTypes';
import { processTimestamps, dateStringToLocalDate, dateToLocalDateString } from 'src/utils/appDateFunctions';
import { gigStatuses } from 'src/constants/appConstants';
import { setGigsByDaySelectedDate } from 'src/store/slices/reportControlsSlice';
import { setActiveEventBasicInfo } from 'src/store/slices/activeEventSlice';
import { AppDispatch } from 'src/store';

const GigsByDay = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const selectedDateString = useSelector((state: RootState) => state.reportControls.gigsByDaySelectedDate);
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Set page title when component mounts
  useEffect(() => {
    dispatch(setActiveEventBasicInfo({
      id: '',
      name: 'Gigs by Day'
    }));
  }, [dispatch]);

  // Convert stored date string to Date object, with fallback to today
  // IMPORTANT: Use utility function to avoid timezone shifts
  // When using new Date(dateString), it creates UTC date which can shift based on local timezone
  const selectedDate = selectedDateString 
    ? dateStringToLocalDate(selectedDateString)
    : new Date();

  // Handle date change and update store
  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) {
      dispatch(setGigsByDaySelectedDate(null));
      return;
    }
    
    // Format date as YYYY-MM-DD using utility function to avoid timezone shifts
    const dateString = dateToLocalDateString(newDate);
    
    dispatch(setGigsByDaySelectedDate(dateString));
  };

  // Handle row click navigation - simpler than all-events since we have eventId directly
    const handleRowClick = async (params: any) => {
    const gig = params.row as Gig;
    if (gig.eventId) {
      try {
        // Fetch the full event data to get the complete title
        const eventDoc = await getDoc(doc(db, 'events', gig.eventId));
        if (eventDoc.exists()) {
          const eventData = eventDoc.data() as FirebaseEvent;
          // Set the event basic info with the real event name from dayliteData
          dispatch(setActiveEventBasicInfo({
            id: gig.eventId,
            name: eventData.dayliteData?.name || eventData.friendlyTitle || gig.eventFriendlyTitle || 'Event'
          }));
        }
      } catch (error) {
        console.error('Error fetching event data:', error);
        // Fallback to using the friendly title if fetch fails
        dispatch(setActiveEventBasicInfo({
          id: gig.eventId,
          name: gig.eventFriendlyTitle || 'Event'
        }));
      }
      navigate(`/event/talent?id=${gig.eventId}`);
    }
  };

  // Set up real-time listener for gigs on the selected date
  useEffect(() => {
    if (!selectedDate || !selectedDateString) {
      setGigs([]);
      return;
    }

    setIsLoading(true);
    
    // Use the selectedDateString directly to avoid any timezone conversion issues
    // This ensures we're querying for the exact date string stored in Firestore (YYYY-MM-DD)
    const dateString = selectedDateString;
    
    // Create query for gigs collection
    const gigsRef = collection(db, 'gigs');
    const gigsQuery = query(
      gigsRef,
      where('eventDate', '==', dateString)
    );

    // Set up the snapshot listener
    const unsubscribe = onSnapshot(gigsQuery, (snapshot) => {
      const gigsData: Gig[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        const processedData = processTimestamps(data);
        gigsData.push({ id: doc.id, ...processedData } as Gig);
      });
      setGigs(gigsData);
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching gigs:', error);
      setIsLoading(false);
    });

    // Clean up listener when component unmounts or date changes
    return () => unsubscribe();
  }, [selectedDateString]); // Use selectedDateString as dependency since that's what changes

  // Column configuration similar to talent view but without editing
  const columns: GridColDef[] = [
    {
      field: 'talentName',
      headerName: 'Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'eventFriendlyTitle',
      headerName: 'Event',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      type: 'singleSelect',
      valueOptions: gigStatuses
    },
    {
      field: 'notes',
      headerName: 'Notes',
      minWidth: 200,
      flex: 1
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      type: 'dateTime',
      width: 180,
      valueGetter: (value) => (value ? new Date(value) : null)
    },
    {
      field: 'updatedBy',
      headerName: 'By',
      width: 150
    },
    {
      field: 'actionLog',
      headerName: 'Action Log',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any, Gig>) => {
        const statusTimestamps: StatusTimestamp[] = (params.row.statusTimestamps || []).map((log: any) => ({
          ...log,
          changedAt:
            log.changedAt instanceof Timestamp
              ? log.changedAt
              : log.changedAt && typeof log.changedAt.toDate === 'function'
                ? log.changedAt.toDate()
                : log.changedAt instanceof Date
                  ? log.changedAt
                  : new Date(0)
        }));
        const hasHistory = statusTimestamps.length > 0;

        const formatTimestamp = (timestamp: Timestamp | Date): string => {
          if (!timestamp) return 'N/A';
          const date = timestamp instanceof Timestamp ? timestamp.toDate() : timestamp;
          return date && !isNaN(date.getTime()) ? date.toLocaleString() : 'Invalid Date';
        };

        const getMilliseconds = (timestamp: Timestamp | Date): number => {
          if (timestamp instanceof Timestamp) {
            return timestamp.toMillis();
          }
          if (timestamp instanceof Date) {
            return timestamp.getTime();
          }
          return 0;
        };

        const tooltipContent = (
          <List dense disablePadding>
            {statusTimestamps
              .slice()
              .sort(
                (a: StatusTimestamp, b: StatusTimestamp) => getMilliseconds(b.changedAt) - getMilliseconds(a.changedAt)
              )
              .map((log: StatusTimestamp, index: number) => (
                <ListItem key={index} disableGutters>
                  <ListItemText
                    primary={`${log.changedTo}`}
                    secondary={`${formatTimestamp(log.changedAt)} by ${log.changedBy}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
          </List>
        );

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            {hasHistory && (
              <Tooltip title={tooltipContent} placement="top" arrow>
                <IconButton size="small" color="info" aria-label="status history">
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <Stack spacing={2}>
      {/* Date Selection Controls */}
      <Stack direction="row" spacing={2} alignItems="center">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={handleDateChange}
            slotProps={{ textField: { style: { minWidth: '200px' } } }}
          />
        </LocalizationProvider>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {gigs.length} {gigs.length === 1 ? 'gig' : 'gigs'} found
        </Typography>
      </Stack>

      {/* Loading indicator */}
      {isLoading && <LinearProgress />}

      {/* Data Grid */}
      <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
        <DataGrid
          rows={gigs}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          density="compact"
          hideFooter
          onRowClick={handleRowClick}
          sx={{
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem'
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer'
            }
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'talentName', sort: 'asc' }]
            }
          }}
        />
      </Box>
    </Stack>
  );
};

export default GigsByDay;
